import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { initializeFirebaseStructures } from './utils/videoUtils';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import VideoPlayerPage from './pages/VideoPlayer';
import Profile from './pages/Profile';
import { AppBar, Toolbar, Avatar, Box, IconButton, Drawer, List, ListItem, ListItemIcon, ListItemText, Divider, ListItemButton, Button, Menu, MenuItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import Logo from './components/common/Logo';
import LanguageSwitcher from './components/common/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

const Footer: React.FC = () => {
  return (
    <Box sx={{ 
      width: '100%', 
      textAlign: 'center', 
      py: 3, 
      bgcolor: '#000000',
      color: '#ffffff',
      fontSize: 15 
    }}>
      &copy; 2025 Le Videolezioni di Martin
    </Box>
  );
};

const MainAppBar: React.FC = () => {
  const { user, role, signOutUser } = useAuth();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleDrawerToggle = () => setDrawerOpen((open) => !open);
  const handleNav = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };
  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/profile');
  };
  const handleLogout = async () => {
    handleMenuClose();
    await signOutUser();
    navigate('/dashboard'); // Always go to dashboard after logout
  };

  return (
    <>
      {/* Floating Drawer Button */}
      <IconButton edge="start" color="inherit" aria-label="menu" onClick={handleDrawerToggle}
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1301, // above AppBar
          bgcolor: 'black',
          boxShadow: 3,
          '&:hover': { bgcolor: '#222' },
        }}
      >
        <MenuIcon />
      </IconButton>
      {/* AppBar (not fixed) */}
      <AppBar position="static" sx={{ 
        bgcolor: '#000000', 
        backgroundColor: '#000000',
        boxShadow: 'none', 
        borderBottom: '1px solid #222',
        opacity: 1,
        backdropFilter: 'none'
      }}>
        <Toolbar sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 72,
          bgcolor: '#000000',
          backgroundColor: '#000000'
        }}>
          {/* Centered logo */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: 140 }}><Logo /></Box>
          </Box>
          {/* User avatar or Log In button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {user ? (
              <>
                <IconButton
                  onClick={handleAvatarClick}
                  size="small"
                  sx={{ 
                    p: 0,
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                >
                  <Avatar 
                    src={user?.photoURL || undefined} 
                    alt={user?.displayName || 'U'} 
                    sx={{ 
                      bgcolor: '#E53935',
                      cursor: 'pointer',
                      border: '2px solid rgba(229, 57, 53, 0.3)',
                      '&:hover': {
                        borderColor: 'rgba(229, 57, 53, 0.6)'
                      },
                      transition: 'all 0.2s ease'
                    }} 
                  />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  onClick={handleMenuClose}
                  PaperProps={{
                    sx: {
                      bgcolor: 'rgba(0, 0, 0, 0.95)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      mt: 1,
                      minWidth: 180
                    }
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem 
                    onClick={handleProfileClick}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        bgcolor: 'rgba(229, 57, 53, 0.15)'
                      }
                    }}
                  >
                    <PersonIcon sx={{ mr: 2, fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                    {t('navigation.profile') || 'Profile'}
                  </MenuItem>
                  <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', my: 0.5 }} />
                  <MenuItem 
                    onClick={handleLogout}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        bgcolor: 'rgba(229, 57, 53, 0.15)'
                      }
                    }}
                  >
                    <LogoutIcon sx={{ mr: 2, fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                    {t('navigation.logout')}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button color="inherit" onClick={() => navigate('/login')} sx={{ color: 'white', fontWeight: 700 }}>
                {t('navigation.logIn')}
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer anchor="left" open={drawerOpen} onClose={handleDrawerToggle}>
        <Box sx={{ width: 260, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'black', color: 'white' }}>
          <Divider sx={{ bgcolor: '#333' }} />
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleNav('/dashboard')}>
                <ListItemIcon sx={{ color: 'white' }}><DashboardIcon /></ListItemIcon>
                <ListItemText primary={t('navigation.dashboard')} />
              </ListItemButton>
            </ListItem>
            {/* Shop temporarily disabled */}
            {/* <ListItem disablePadding>
              <ListItemButton onClick={() => handleNav('/shop')}>
                <ListItemIcon sx={{ color: 'white' }}><StoreIcon /></ListItemIcon>
                <ListItemText primary={t('navigation.shop')} />
              </ListItemButton>
            </ListItem> */}
            {role === 'admin' && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNav('/admin')}>
                  <ListItemIcon sx={{ color: 'white' }}><AdminPanelSettingsIcon /></ListItemIcon>
                  <ListItemText primary={t('navigation.adminDashboard')} />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

const ProtectedAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { role, loading } = useAuth();
  if (loading) return null;
  if (role !== 'admin') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

function AppRoutes() {
  const location = useLocation();
  // Only show AppBar and Footer on dashboard/admin/shop/video/profile
  const showAppBar = ['/dashboard', '/admin', '/shop', '/video', '/profile'].some(path => location.pathname.startsWith(path));
  const showFooter = ['/dashboard', '/admin', '/shop', '/video', '/profile'].some(path => location.pathname.startsWith(path));
  return (
    <>
      {showAppBar && <MainAppBar />}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/video/:videoId" element={<VideoPlayerPage />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/admin" element={
          <ProtectedAdminRoute>
            <AdminDashboard />
          </ProtectedAdminRoute>
        } />
        {/* Shop temporarily disabled - redirects to dashboard */}
        <Route path="/shop" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      {showFooter && <Footer />}
      {/* Language Switcher - always visible */}
      <LanguageSwitcher />
    </>
  );
}

function App() {
  // Initialize Firebase structures on app startup
  useEffect(() => {
    const initFirebase = async () => {
      try {
        await initializeFirebaseStructures();
        console.log('Firebase structures initialized on app startup');
      } catch (error) {
        console.error('Error initializing Firebase structures on startup:', error);
      }
    };
    
    initFirebase();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
